import React from 'react';
import './StatusBadge.css';

export type StatusBadgeTone =
  | 'draft'
  | 'sent'
  | 'pending'
  | 'accepted'
  | 'overdue'
  | 'completed'
  | 'locked'
  | 'cancelled'
  | 'default';

export interface StatusBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  status?: string | null;
  size?: 'sm' | 'md' | 'lg';
  children?: React.ReactNode;
}

function normalizeStatus(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

export function getStatusBadgeTone(status?: string | null): StatusBadgeTone {
  const normalized = normalizeStatus(status);

  if (!normalized) return 'default';
  if (['draft', 'new', 'planning'].includes(normalized)) return 'draft';
  if (['sent', 'filed'].includes(normalized)) return 'sent';
  if (['pending', 'in progress', 'review', 'in review', 'active', 'open'].includes(normalized)) return 'pending';
  if (['accepted', 'approved', 'paid', 'issued'].includes(normalized)) return 'accepted';
  if (['overdue', 'expired'].includes(normalized)) return 'overdue';
  if (['completed', 'complete', 'done', 'finished'].includes(normalized)) return 'completed';
  if (['locked', 'final', 'finalised', 'finalized', 'on hold', 'paused', 'archived', 'inactive'].includes(normalized)) return 'locked';
  if (['cancelled', 'canceled', 'void', 'rejected', 'declined', 'superseded'].includes(normalized)) return 'cancelled';

  return 'default';
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'md',
  className = '',
  children,
  ...props
}) => {
  const tone = getStatusBadgeTone(status);
  const classNames = [
    'ui-status-badge',
    `ui-status-badge--${tone}`,
    `ui-status-badge--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classNames} {...props}>
      {children ?? status ?? 'Unknown'}
    </span>
  );
};

StatusBadge.displayName = 'StatusBadge';
